import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BsArrowRight, BsCheck2 } from "react-icons/bs";
import { FiBox, FiCompass, FiFeather, FiHome, FiLayers, FiTool } from "react-icons/fi";

const principles = [
  {
    title: "Designed around you",
    text: "Every plan begins with how you live, what you value and how you want the space to feel.",
  },
  {
    title: "One considered vision",
    text: "Layouts, materials and furniture are selected together so the finished home feels coherent.",
  },
  {
    title: "Clear from the start",
    text: "We align the scope, selections and next steps before execution begins.",
  },
];

const capabilities = [
  {
    icon: FiCompass,
    title: "Interior planning",
    text: "Layouts shaped around movement, comfort, storage and the way each room is used.",
  },
  {
    icon: FiLayers,
    title: "Material selection",
    text: "A coordinated palette of finishes, colours and textures for a cohesive result.",
  },
  {
    icon: FiBox,
    title: "Curated furniture",
    text: "Furniture chosen to balance proportion, function and the character of your home.",
  },
  {
    icon: FiTool,
    title: "Managed execution",
    text: "Design decisions and on-site work coordinated through one accountable team.",
  },
  {
    icon: FiHome,
    title: "Residential spaces",
    text: "Thoughtful solutions for living rooms, bedrooms, dining spaces and complete homes.",
  },
  {
    icon: FiFeather,
    title: "Responsible choices",
    text: "Durable materials and practical decisions considered for long-term everyday use.",
  },
];

const process = [
  ["01", "Discover", "We understand your space, priorities, taste and expected timeline."],
  ["02", "Define", "Layouts, materials, furniture and project scope are brought into focus."],
  ["03", "Create", "The approved direction is coordinated through design and execution."],
  ["04", "Complete", "Finishing details are reviewed before the space is handed over."],
];

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-white text-gray-900">
      <section className="site-container relative h-[42vh] min-h-[240px] max-h-[420px] overflow-hidden lg:h-[50vh] lg:min-h-[300px] lg:max-h-[520px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/IMG_4975.png')",
            backgroundPosition: "0% 100%",
            backgroundSize: "300% 200%",
          }}
          role="img"
          aria-label="Contemporary living room by Fina Interiors"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative flex h-full items-end py-10 md:py-14">
          <div className="max-w-3xl text-white">
            <p className="text-xs font-bold uppercase text-[#d3ac9e]">About Fina Interiors</p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight md:text-6xl">
              Homes with clarity, character and purpose
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-gray-200 md:text-lg">
              We bring interior design, furniture and coordinated execution together
              to create spaces that feel personal, functional and beautifully resolved.
            </p>
          </div>
        </div>
      </section>

      <section className="site-container grid grid-cols-1 gap-10 py-14 md:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div>
          <p className="text-xs font-bold uppercase text-[#858653]">Our approach</p>
          <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
            A complete interior story, not disconnected choices
          </h2>
        </div>
        <div className="space-y-5 text-base leading-7 text-gray-600">
          <p>
            Fina Interiors creates residential spaces and furniture solutions with a
            simple belief: good design should look considered and work naturally in
            everyday life.
          </p>
          <p>
            Our team studies the space, understands your priorities and coordinates
            the details from concept through handover. The result is a home that feels
            cohesive, comfortable and distinctly yours.
          </p>
        </div>
      </section>

      <section className="border-y border-gray-200 bg-[#f5f3f1] py-14 md:py-20">
        <div className="site-container">
          <div className="grid grid-cols-1 gap-px border border-gray-200 bg-gray-200 md:grid-cols-3">
            {principles.map((principle) => (
              <article key={principle.title} className="bg-white p-7 md:p-9">
                <BsCheck2 className="text-[#858653]" size={28} aria-hidden="true" />
                <h2 className="mt-5 text-xl font-semibold">{principle.title}</h2>
                <p className="mt-3 text-sm leading-6 text-gray-600">{principle.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="site-container py-14 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase text-[#858653]">What we do</p>
          <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
            Design, furniture and execution under one roof
          </h2>
          <p className="mt-4 text-base leading-7 text-gray-600">
            From an individual furniture piece to a complete home interior, our work
            connects every decision to one clear design direction.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 border-l border-t border-gray-200 sm:grid-cols-2 lg:grid-cols-3">
          {capabilities.map(({ icon: Icon, title, text }) => (
            <article key={title} className="border-b border-r border-gray-200 p-6 md:p-8">
              <Icon size={24} className="text-[#858653]" aria-hidden="true" />
              <h3 className="mt-5 text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#20211d] text-white">
        <div className="site-container grid grid-cols-1 lg:grid-cols-2">
          <div
            className="min-h-[340px] bg-cover bg-center lg:min-h-[560px]"
            style={{
              backgroundImage: "url('/IMG_4975.png')",
              backgroundPosition: "50% 0%",
              backgroundSize: "300% 200%",
            }}
            role="img"
            aria-label="Fina Interiors living room design"
          />
          <div className="flex flex-col justify-center py-12 md:py-16 lg:px-10">
            <p className="text-xs font-bold uppercase text-[#c49a8c]">Our process</p>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
              A clear path from first idea to finished space
            </h2>
            <div className="mt-8 divide-y divide-white/15 border-y border-white/15">
              {process.map(([number, title, text]) => (
                <div key={number} className="grid grid-cols-[42px_1fr] gap-4 py-5">
                  <span className="text-sm font-semibold text-[#a6a76c]">{number}</span>
                  <div>
                    <h3 className="font-semibold">{title}</h3>
                    <p className="mt-1 text-sm leading-6 text-gray-400">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="site-container grid grid-cols-1 gap-10 py-14 md:py-20 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        <div>
          <p className="text-xs font-bold uppercase text-[#858653]">Our team</p>
          <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
            Different skills, one shared standard
          </h2>
        </div>
        <div className="space-y-5 text-base leading-7 text-gray-600">
          <p>
            Our designers and execution professionals work as one coordinated team.
            That continuity helps decisions remain consistent from the first layout to
            the final finishing detail.
          </p>
          <p>
            We value practical communication, considered craftsmanship and respect for
            the customer&apos;s home. Every project is approached with care, clarity and
            attention to how the space will be lived in after handover.
          </p>
        </div>
      </section>

      <section className="site-container grid grid-cols-1 bg-[#20211d] text-white lg:grid-cols-2">
        <div className="py-12 md:py-16">
          <p className="text-xs font-bold uppercase text-[#c49a8c]">Begin your project</p>
          <h2 className="mt-3 text-3xl font-semibold md:text-4xl">
            Let us shape a space that feels like home
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-6 text-gray-300 md:text-base">
            Tell us about your home, timeline and ideas. Our Pune team will help you
            understand the right next step.
          </p>
          <Link
            to="/get-quote"
            className="mt-7 inline-flex items-center gap-2 bg-[#858653] px-5 py-3 text-sm font-semibold hover:bg-[#747548]"
          >
            Request a consultation <BsArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div
          className="min-h-[300px] bg-cover bg-center"
          style={{
            backgroundImage: "url('/IMG_4975.png')",
            backgroundPosition: "100% 0%",
            backgroundSize: "300% 200%",
          }}
          role="img"
          aria-label="Refined living room interior"
        />
      </section>
    </main>
  );
};

export default AboutUs;
